import React from "react";
import { Link } from "react-router-dom";

import digitalTwin from "../assets/screenshots/My Shipment Digital Twin.png";
import blockchain from "../assets/screenshots/Blockchain Explorer.png";
import iotDevices from "../assets/screenshots/IoT Devices.png";
import iotMap from "../assets/screenshots/Iot Map.png";
import forecast from "../assets/screenshots/Demand Forecast.png";
import iotLive from "../assets/screenshots/IoT Live Control Center.png";

const screenshots = [
    [
        "Digital Twin",
        digitalTwin,
        "Virtual shipment state, telemetry and operational monitoring."
    ],
    [
        "Blockchain Explorer",
        blockchain,
        "Immutable audit trail for transparent logistics operations."
    ],
    [
        "IoT Devices",
        iotDevices,
        "Connected sensor and device monitoring."
    ],
    [
        "IoT Map",
        iotMap,
        "Map-based visualization of logistics assets."
    ],
    [
        "Demand Forecast",
        forecast,
        "Demand prediction based on shipment activity."
    ],
    [
        "IoT Live Control Center",
        iotLive,
        "Operational IoT monitoring dashboard."
    ],
];

const modules = [
    [
        "🔐",
        "Identity-Based Security",
        "Secure authentication, authorization and encrypted data exchange."
    ],
    [
        "⛓️",
        "Blockchain Audit",
        "Immutable operational history and verification."
    ],
    [
        "🤖",
        "AI Analytics",
        "Anomaly detection, prediction and intelligent decision support."
    ],
    [
        "📡",
        "IoT Monitoring",
        "Live telemetry from shipment devices and sensors."
    ],
    [
        "🛰️",
        "Digital Twin",
        "Virtual representation and monitoring of shipment state."
    ],
    [
        "📈",
        "Predictive Analytics",
        "Data-driven forecasting for logistics operations."
    ],
];

const ShowcasePage = () => {
    return (
        <div className="w-screen min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">

            {/* HERO */}
            <section className="w-full bg-gradient-to-br from-white via-blue-50 to-slate-100 border-b border-blue-100">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16 py-24 grid lg:grid-cols-2 gap-16 items-center">

                    <div>
                        <p className="uppercase tracking-[0.25em] text-blue-700 font-bold mb-5">
                            Secure Transparent Data Exchange
                        </p>

                        <h1 className="text-5xl lg:text-7xl font-black leading-tight mb-6">
                            IDLogicChain
                        </h1>

                        <p className="text-xl text-slate-700 leading-relaxed max-w-3xl">
                            Intelligent logistics platform integrating Artificial
                            Intelligence, Blockchain, IoT telemetry, Digital Twin
                            technology and Identity-Based Security for secure logistics
                            operations.
                        </p>

                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link
                                to="/login"
                                className="bg-blue-700 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-blue-800 transition"
                            >
                                Login
                            </Link>

                            <Link
                                to="/register"
                                className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:bg-green-600 transition"
                            >
                                Register
                            </Link>

                            <Link
                                to="/platform-modules"
                                className="bg-white text-blue-700 border border-blue-200 px-8 py-4 rounded-xl font-bold shadow hover:bg-blue-50 transition"
                            >
                                Platform Modules
                            </Link>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl shadow-2xl border border-blue-100 p-8">
                        <h2 className="text-2xl font-bold mb-8">
                            Platform Status
                        </h2>

                        <div className="grid grid-cols-2 gap-5">
                            <StatusCard title="AI" value="Enabled" />
                            <StatusCard title="Digital Twin" value="Online" />
                            <StatusCard title="Blockchain" value="Verified" />
                            <StatusCard title="IoT" value="Online" />
                        </div>

                        <p className="text-green-700 font-semibold mt-8">
                            ● Research prototype successfully demonstrated
                        </p>
                    </div>

                </div>
            </section>

            {/* TECHNOLOGY STATUS */}
            <section className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16 py-16">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Metric
                        icon="🤖"
                        title="Artificial Intelligence"
                        value="Enabled"
                    />

                    <Metric
                        icon="📡"
                        title="IoT Telemetry"
                        value="Active"
                    />

                    <Metric
                        icon="⛓️"
                        title="Blockchain Audit"
                        value="Verified"
                    />

                    <Metric
                        icon="🛰️"
                        title="Digital Twin"
                        value="Online"
                    />
                </div>
            </section>

            {/* MODULES */}
            <section className="bg-white py-20">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16">

                    <h2 className="text-4xl lg:text-5xl font-black text-center mb-5">
                        Platform Modules
                    </h2>

                    <p className="text-center text-slate-600 max-w-4xl mx-auto mb-14 text-lg">
                        One integrated research prototype combining security,
                        transparency, intelligent analysis, monitoring and digital
                        shipment modelling.
                    </p>

                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                        {modules.map(([icon, title, text]) => (
                            <div
                                key={title}
                                className="bg-slate-50 rounded-3xl p-8 border border-slate-200 shadow hover:shadow-xl transition"
                            >
                                <div className="text-5xl mb-5">
                                    {icon}
                                </div>

                                <h3 className="text-2xl font-bold mb-3">
                                    {title}
                                </h3>

                                <p className="text-slate-600 leading-relaxed">
                                    {text}
                                </p>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* DEMONSTRATION */}
            <section className="bg-slate-100 py-20">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16">

                    <h2 className="text-4xl lg:text-5xl font-black text-center mb-12">
                        Platform Demonstration
                    </h2>

                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                        {screenshots.map(([title, image, text]) => (
                            <div
                                key={title}
                                className="bg-white rounded-3xl overflow-hidden shadow-xl border border-slate-200 hover:shadow-2xl transition"
                            >
                                <img
                                    src={image}
                                    alt={title}
                                    className="w-full h-56 object-cover"
                                />

                                <div className="p-6">
                                    <h3 className="text-xl font-bold mb-2">
                                        {title}
                                    </h3>

                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {text}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* ARCHITECTURE */}
            <section className="bg-white py-20">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16">

                    <h2 className="text-4xl lg:text-5xl font-black text-center mb-12">
                        Platform Architecture
                    </h2>

                    <div className="bg-slate-50 rounded-3xl shadow-xl border border-slate-200 p-8 lg:p-12">

                        <div className="grid lg:grid-cols-5 gap-6 text-center font-bold items-center">
                            <ArchBox
                                text="React SPA"
                                color="bg-blue-100 text-blue-900"
                            />

                            <div className="text-5xl hidden lg:block">
                                ➜
                            </div>

                            <ArchBox
                                text="ASP.NET Core API"
                                color="bg-green-100 text-green-900"
                            />

                            <div className="text-5xl hidden lg:block">
                                ➜
                            </div>

                            <ArchBox
                                text="SQL Server"
                                color="bg-purple-100 text-purple-900"
                            />
                        </div>

                        <div className="grid md:grid-cols-4 gap-6 mt-10">
                            {[
                                "ML.NET",
                                "Blockchain Logs",
                                "IoT Devices",
                                "Digital Twin"
                            ].map((item) => (
                                <div
                                    key={item}
                                    className="bg-white rounded-2xl p-6 shadow text-center font-bold"
                                >
                                    {item}
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            </section>

            {/* RESEARCH */}
            <section className="bg-gradient-to-r from-blue-950 to-slate-900 text-white py-20">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16 text-center">

                    <h2 className="text-4xl lg:text-5xl font-black mb-12">
                        Research & Innovation
                    </h2>

                    <div className="max-w-4xl mx-auto text-left">
                        <InfoCard
                            title="Scientific Contribution"
                            text="The prototype demonstrates integration of Artificial Intelligence, Blockchain, Identity-Based Security, IoT telemetry and Digital Twin technology inside one secure logistics ecosystem."
                        />
                    </div>

                </div>
            </section>

            {/* ROADMAP */}
            <section className="bg-white py-20">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16">

                    <h2 className="text-4xl lg:text-5xl font-black text-center mb-12">
                        Development Roadmap
                    </h2>

                    <div className="grid md:grid-cols-5 gap-6">
                        {[
                            ["2025", "Master Research"],
                            ["2026", "Platform Demo"],
                            ["Next", "Further Development"],
                            ["Future", "Industrial Pilot"],
                            ["Vision", "Smart Logistics"],
                        ].map(([year, text]) => (
                            <div
                                key={`${year}-${text}`}
                                className="bg-slate-100 rounded-2xl p-8 text-center shadow"
                            >
                                <p className="text-blue-700 font-black text-2xl">
                                    {year}
                                </p>

                                <p className="font-semibold mt-3">
                                    {text}
                                </p>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-slate-950 text-slate-300 py-12">
                <div className="w-full max-w-screen-2xl mx-auto px-6 lg:px-16 grid md:grid-cols-3 gap-10">

                    <div>
                        <h3 className="text-white text-2xl font-bold mb-3">
                            🛡 IDLogicChain
                        </h3>

                        <p>
                            Secure Transparent Data Exchange Platform
                        </p>
                    </div>

                    <div>
                        <h3 className="text-white font-bold mb-3">
                            Technologies
                        </h3>

                        <p>
                            React • ASP.NET Core • SQL Server • ML.NET • Docker
                        </p>
                    </div>

                    <div>
                        <h3 className="text-white font-bold mb-3">
                            Project Status
                        </h3>

                        <p>
                            Research Prototype • Cloud Demo • Platform Development
                        </p>
                    </div>

                </div>
            </footer>

        </div>
    );
};

const StatusCard = ({ title, value }) => (
    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
        <p className="text-slate-500">
            {title}
        </p>

        <h3 className="text-3xl font-black mt-2">
            {value}
        </h3>
    </div>
);

const Metric = ({ icon, title, value }) => (
    <div className="bg-white rounded-3xl p-7 shadow-lg border border-slate-200">
        <div className="text-5xl mb-4">
            {icon}
        </div>

        <p className="text-slate-500">
            {title}
        </p>

        <h3 className="text-2xl font-black mt-2">
            {value}
        </h3>
    </div>
);

const ArchBox = ({ text, color }) => (
    <div className={`${color} rounded-2xl p-8 shadow font-black`}>
        {text}
    </div>
);

const InfoCard = ({ title, text }) => (
    <div className="bg-white/10 rounded-3xl p-8 border border-white/20 shadow-xl">
        <h3 className="text-2xl font-bold mb-4">
            {title}
        </h3>

        <p className="text-blue-100 leading-relaxed">
            {text}
        </p>
    </div>
);

export default ShowcasePage;